import styles from './ModernApplicationDevelopment.module.scss'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

const tweenVars: gsap.TimelineVars = {
  y: '50',
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

const ModernApplicationDevelopment: React.FC = () => {
  const timeline = gsap.timeline()

  const EN_title = useRef<HTMLDivElement>(null)
  const JP_title = useRef<HTMLDivElement>(null)
  const text = useRef<HTMLDivElement>(null)
  const img = useRef<HTMLImageElement>(null)
  const supports = useRef<HTMLDivElement[]>([])

  useEffect(() => {
    timeline
      .from(img.current, tweenVars, 0.1)
      .from(EN_title.current, tweenVars, 0.1)
      .from(JP_title.current, tweenVars, 0.3)
      .from(text.current, tweenVars, 0.5)

    // 残りの要素をstaggerで順番にアニメーションさせる
    timeline.from(supports.current, {
      ...tweenVars,
      stagger: 0.2,
    })

    ScrollTrigger.create({
      animation: timeline,
      trigger: EN_title.current,
      start: 'top-=150 80%',
      toggleActions: 'play none none reverse',
    })

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill())
      timeline.kill()
    }
  }, [])

  const services = [
    {
      href: '/services/studio/application-development/',
      title: 'アプリケーション開発',
      body: 'クラウドネイティブアーキテクチャの利点を最大限に活用し、アジャイル開発手法を効果的に適用しながら、優れたUX/UIを実現するモダンなアプリケーション開発を提供します。',
    },
    {
      href: '/services/devops',
      title: 'DevOps構築',
      body: '内製化支援とDevOps導入でCI/CDとIaCを活用し、GitLabで継続的統合・デプロイを実現。自動化テストとスケーラブルなインフラで、迅速なリリースと高品質なソフトウェアを提供します。',
    },
    {
      href: '/services/cloud',
      title: 'クラウド導入支援',
      body: 'AWS等のクラウドサービスを基盤として、既存システムをクラウドネイティブなマイクロサービスアーキテクチャに移行します。内製化支援により、柔軟性とスケーラビリティを兼ね備えたクラウド基盤の構築支援を提供します。',
    },
    {
      href: '/services/studio/design-system/',
      title: 'デザインシステム構築支援',
      body: 'デザイン原則の定義、スタイルガイド作成、UIデザインの最適化、UIコンポーネント設計からフロントエンド開発まで、デザインシステム構築の総合的なサポート、また内製化の支援が可能です。',
    },
    {
      href: '/services/uiuxdesign',
      title: 'UX/UIデザイン',
      body: 'モバイルアプリ、SaaSプロダクト、業務アプリケーションやECサイトなど幅広いプロダクトのUIを設計いたします。OOUI（オブジェクト指向UI）を取り入れた設計でユーザビリティを徹底的に追求。さらに、効率的で一貫性のある開発をサポートするデザインシステムの構築も可能です。',
    },
    {
      href: '/services/product-management',
      title: 'プロダクトマネジメント',
      body: 'プロダクト開発の手法を駆使して、デジタルプロダクトの立ち上げからグロースフェーズまで、サービス開発を総合的に支援します。',
    },
  ]

  return (
    <section className={styles.contents} id="modern-application-development">
      <div className={styles.head}>
        <figure className={styles.head_icon}>
          <img ref={img} src="/images/icons/icon_modern_application.svg" alt="Digital Innovation Support" />
        </figure>
        <div className={styles.head_title}>
          <h2 ref={EN_title} className={styles.head_title_en}>
            Modern application <br className={styles.is_sp} />
            development
          </h2>
          <h3 ref={JP_title} className={styles.head_title_ja}>
            モダンアプリケーション・デベロップメント
          </h3>
        </div>
      </div>
      <p ref={text} className={styles.head_title_text}>
        モバイルアプリ、SaaSプロダクト、業務システムなどのソフトウエアを、洗練されたUX/UIデザインで、クラウドネイティブなアーキテクチャでの設計・実装をするモダンアプリケーション開発を行っています。開発プロセスは基本的にアジャイルで、AWSマネージドサービスを活用したCI/CDパイプライン管理、コンテナ化ソリューションなど継続的なサービス開発を考慮したDevOps環境、デザインシステムの構築と顧客企業の内製化を伴走サポートします。
      </p>
      <div className={styles.supports}>
        {services.map((service, index) => (
          <div key={index} ref={(el) => (supports.current[index] = el)} className={styles.support}>
            <a href={service.href}>
              <div className={styles.wrapper}>
                <span className={styles.dot}></span>
                <div className={styles.textWrapper}>
                  <h4 className={styles.title}>{service.title}</h4>
                  <p className={styles.description}>{service.body}</p>
                </div>
              </div>
              <span className={styles.viewmore}>Read more</span>
            </a>
          </div>
        ))}
      </div>
    </section>
  )
}

export default ModernApplicationDevelopment
